import {Fragment} from 'react';
// @ts-expect-error
import type {Control, Watch} from 'react-hook-form';

import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';

import {LoadingField} from '../../../editor/preview/loading-field';

import type {FormFieldSchema} from '../../../../../core/nutshell-fields/types';
import {getInputStyles} from '../../../../helpers';
import type {ActiveField} from '../../../../types';

import type {FieldWithArrayKey} from '../../sidebar/blocks-and-design/content-blocks/types';
import type {SidebarState} from '../../sidebar/sidebar';

import {isPageContentEmpty} from '../helpers';

import {FieldInsertZone} from './field-insert-zone';
import {getPageFieldComponents} from './get-page-field-components';
import {PageFooter} from './page-footer';
import {SortableFieldItem} from './sortable-field-item';

import styles from '../form-editor.module.css';

interface Props {
    pageIndex: number;
    fields: FieldWithArrayKey[];
    watch: Watch;
    control: Control;
    onRemoveField: (index: number) => void;
    onRemovePage: (pageIndex: number) => void;
    setActiveField: (arg1?: ActiveField | null | undefined) => void;
    activeField: ActiveField | null | undefined;
    formSchema: FormFieldSchema;
    fieldCreateAtIndex: {pageIndex: number; insertIndex: number} | null;
    setSidebarState: (state: SidebarState) => void;
    hasMoreThanOnePage: boolean;
    setActivePageIndex: (pageIndex: number | null | undefined) => void;
}

export function FormEditorPage(props: Props) {
    const fontStyle = props.watch('fontStyle');
    const fontFamily = fontStyle ? fontStyle.fontFamily : undefined;
    const borderRadius = props.watch('inputStyle.borderRadius');
    const border = props.watch('inputStyle.border');
    const fontSize = props.watch('inputStyle.fontSize');
    const color = props.watch('inputStyle.color');
    const isFieldCreatePending = props.fieldCreateAtIndex !== null;

    const isEmptyPage = isPageContentEmpty(props.fields);

    const loadingFieldProps = {
        fontSize,
        placeholderStyles: getInputStyles({
            borderRadius,
            border,
            fontSize,
            color,
        }),
    };

    const fieldPathPrefix = `pages.${props.pageIndex}.fields`;

    const fieldComponents = getPageFieldComponents({
        fieldPathPrefix,
        pageIndex: props.pageIndex,
        fields: props.fields,
        formSchema: props.formSchema,
        activeField: props.activeField,
        setActiveField: props.setActiveField,
        watch: props.watch,
        control: props.control,
        onRemoveField: props.onRemoveField,
    });

    const fieldIds = props.fields.map((f) => f.key);

    const renderInsertGap = (insertIndex: number) => {
        const fieldCreatePendingHere =
            props.fieldCreateAtIndex != null &&
            props.fieldCreateAtIndex.pageIndex === props.pageIndex &&
            props.fieldCreateAtIndex.insertIndex === insertIndex;

        if (fieldCreatePendingHere) {
            return (
                <div className={styles.field} key={`field-create-loading-${insertIndex}`}>
                    <LoadingField {...loadingFieldProps} width={200} />
                </div>
            );
        }
        return (
            <FieldInsertZone
                key={`insert-zone-${insertIndex}`}
                pageIndex={props.pageIndex}
                index={insertIndex}
                fieldCreatePending={isFieldCreatePending}
                isEmptyPage={insertIndex === 0 && isEmptyPage}
            />
        );
    };

    return (
        <div className={styles['canvas-card']} onClick={(e) => e.stopPropagation()}>
            <div style={{fontFamily}} className={styles['draggable-list-container']}>
                <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
                    {renderInsertGap(0)}
                    {!isEmptyPage &&
                        fieldComponents.map((item, index) => (
                            <Fragment key={item.id}>
                                <SortableFieldItem
                                    id={item.id}
                                    disabled={isFieldCreatePending}
                                    isActive={item.isActive ?? false}
                                >
                                    {item.component}
                                </SortableFieldItem>
                                {renderInsertGap(index + 1)}
                            </Fragment>
                        ))}
                </SortableContext>
            </div>
            {props.hasMoreThanOnePage && (
                <PageFooter
                    setSidebarState={props.setSidebarState}
                    pageIndex={props.pageIndex}
                    onRemovePage={props.onRemovePage}
                    setActivePageIndex={props.setActivePageIndex}
                />
            )}
        </div>
    );
}
